import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Grid3x3, Snowflake, Sprout, ChefHat, ArrowUpRight, Star } from "lucide-react";
import { Reveal } from "../components/Reveal";
import QuoteForm from "../components/QuoteForm";
import { company, story, services, testimonials, galleryImages } from "../data/site";

const iconMap = { Grid3x3, Snowflake, Sprout, ChefHat };

const Home = () => {
  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative h-screen min-h-[640px] flex items-center overflow-hidden">
        <img src={galleryImages[0]} alt="Fresh Landscaping project" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-forest-deep/85 via-forest-deep/55 to-forest-deep/25" />
        <div className="container-fl relative">
          <motion.img
            src={company.logo}
            alt="Fresh Landscaping logo"
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 0.7 }}
            className="h-28 md:h-40 w-auto mb-6 drop-shadow-2xl"
          />
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.15 }} className="overline text-clay mb-6">
            {company.location} · Every Season
          </motion.p>
          <motion.h1 initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.25 }} className="font-serif text-6xl md:text-8xl text-bone leading-[0.98] max-w-4xl">
            Transforming outdoor spaces with quality care.
          </motion.h1>
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.4 }} className="mt-8 text-bone/85 text-xl max-w-xl font-light">
            Full-service landscaping, hardscaping and snow removal built to last — from design to maintenance.
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.55 }} className="mt-10 flex flex-col sm:flex-row gap-4">
            <Link to="/contact" data-testid="hero-quote-button" className="btn-primary group">
              Get a Free Quote <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={18} />
            </Link>
            <Link to="/gallery" className="border border-bone/40 text-bone px-8 py-4 font-medium tracking-wide hover:bg-bone/10 transition-colors">
              View Our Work
            </Link>
          </motion.div>
        </div>
      </section>

      {/* STORY */}
      <section data-testid="about-section" className="py-24 md:py-32 bg-bone">
        <div className="container-fl grid md:grid-cols-12 gap-12 md:gap-20 items-center">
          <Reveal className="md:col-span-7">
            <p className="overline mb-5">Our Story</p>
            <h2 className="text-4xl md:text-5xl leading-tight mb-8">
              Beautiful outdoor spaces, built to last through every season.
            </h2>
            <p className="text-lg text-moss leading-relaxed">{story}</p>
            <Link to="/contact" className="btn-secondary mt-10">Start Your Project</Link>
          </Reveal>
          <Reveal delay={0.15} className="md:col-span-5">
            <img src={galleryImages[0]} alt="Landscaping project" className="w-full aspect-[4/5] object-cover" />
          </Reveal>
        </div>
      </section>

      {/* SERVICES — bento */}
      <section data-testid="services-section" className="py-24 md:py-32 bg-sand">
        <div className="container-fl">
          <Reveal className="max-w-3xl mb-16">
            <p className="overline mb-5">What We Do</p>
            <h2 className="text-4xl md:text-5xl leading-tight">
              Expert care for every corner of your property.
            </h2>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
            {services.map((s, i) => {
              const Icon = iconMap[s.icon];
              const span = i === 0 || i === 3 ? "md:col-span-7" : "md:col-span-5";
              return (
                <Reveal key={s.slug} delay={i * 0.08} className={span}>
                  <Link to={`/${s.slug}`} data-testid={`service-card-${s.slug}`} className="group relative block overflow-hidden h-[360px] md:h-[400px]">
                    <img src={s.image} alt={s.title} className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                    <div className="absolute inset-0 bg-gradient-to-t from-forest-deep/90 via-forest-deep/25 to-transparent" />
                    <div className="absolute inset-0 p-8 flex flex-col justify-end">
                      <Icon className="text-clay mb-4" size={30} strokeWidth={1.4} />
                      <h3 className="text-bone text-3xl mb-2">{s.title}</h3>
                      <p className="text-bone/75 max-w-md">{s.blurb}</p>
                      <span className="mt-5 inline-flex items-center gap-2 text-bone text-sm font-medium tracking-wide">
                        Learn more <ArrowUpRight size={16} className="group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
                      </span>
                    </div>
                  </Link>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* GALLERY PREVIEW */}
      <section data-testid="gallery-preview" className="py-24 md:py-32 bg-bone">
        <div className="container-fl">
          <Reveal className="flex flex-wrap items-end justify-between gap-6 mb-14">
            <div>
              <p className="overline mb-5">Our Work</p>
              <h2 className="text-4xl md:text-5xl leading-tight max-w-xl">Built by inspiration, finished with care.</h2>
            </div>
            <Link to="/gallery" className="btn-secondary">View Full Gallery</Link>
          </Reveal>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {galleryImages.slice(0, 8).map((img, i) => (
              <Reveal key={i} delay={(i % 4) * 0.06}>
                <div className="overflow-hidden aspect-square group">
                  <img src={img} alt={`Project ${i + 1}`} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section id="reviews" data-testid="testimonials-section" className="py-24 md:py-32 bg-forest-deep text-bone">
        <div className="container-fl scroll-mt-24">
          <Reveal className="max-w-3xl mb-16">
            <p className="overline text-clay mb-5">Customer Reviews</p>
            <h2 className="text-4xl md:text-5xl text-bone leading-tight">Our proof of excellence.</h2>
          </Reveal>
          <div className="columns-1 md:columns-3 gap-8 space-y-8">
            {testimonials.map((t, i) => (
              <Reveal key={i} delay={(i % 3) * 0.1}>
                <div className="border border-bone/15 p-8 break-inside-avoid">
                  <div className="flex gap-1 mb-5 text-clay">
                    {[...Array(t.rating)].map((_, k) => <Star key={k} size={16} fill="currentColor" strokeWidth={0} />)}
                  </div>
                  {t.quote.split("\n\n").map((p, k) => (
                    <p key={k} className="text-bone/80 leading-relaxed mb-3 last:mb-0">{p}</p>
                  ))}
                  <div className="mt-6 pt-5 border-t border-bone/10">
                    <p className="font-serif text-xl text-bone">{t.name}</p>
                    <p className="text-xs text-bone/50 tracking-wide mt-1">recommends Fresh Landscaping · {t.date}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CONTACT */}
      <section id="contact" data-testid="contact-section" className="py-24 md:py-32 bg-sand">
        <div className="container-fl grid md:grid-cols-12 gap-16">
          <Reveal className="md:col-span-5">
            <p className="overline mb-5">Contact Us</p>
            <h2 className="text-4xl md:text-5xl leading-tight mb-6">Get your free quote.</h2>
            <p className="text-lg text-moss leading-relaxed">
              We stay in constant communication with our customers until the job is done. To get a free quote, or if you have questions or special requests, just drop us a line.
            </p>
            <a href={company.phoneHref} className="mt-8 inline-block font-serif text-3xl text-forest hover:text-clay transition-colors">
              {company.phone}
            </a>
          </Reveal>
          <Reveal delay={0.15} className="md:col-span-7 bg-white p-8 md:p-12 border border-line">
            <QuoteForm />
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default Home;
